import type { MouseEvent } from 'react';
import React, { useMemo, useCallback } from 'react';

import {
  TitleSmallText,
  H1Text,
  FontEmphasis,
  FontColor,
} from '@prisjakt/ui/text/variants';
import { Lazy } from 'src/components/lazy';
import { MediaButton } from 'src/components/media-button';

import * as S from './styles';

import type { FacetLink } from './types';

type Props = {
  link: FacetLink;
  selected: boolean;
  onClickPreventDefault: boolean;
  lazyLoadImage?: boolean;
  Component: React.ComponentType<
    React.PropsWithChildren<React.PropsWithChildren<S.ListItemProps>>
  >;
  onClick?: (link: FacetLink) => void;
};

const NavigationLink = ({
  link,
  selected,
  onClickPreventDefault,
  Component,
  onClick,
  lazyLoadImage = true,
}: Props) => {
  const onClickHandler = useCallback(
    (e: MouseEvent) => {
      // Help google with a-links, but handle filter updates via actions
      if (onClick) {
        if (onClickPreventDefault) {
          e.preventDefault();
        }
        onClick(link);
      }
    },
    [link, onClick, onClickPreventDefault]
  );

  const children = useMemo(() => {
    const image = link.imageUrl;

    if (!image) {
      return (
        <H1Text container="span" emphasis={FontEmphasis.LOW}>
          {link.value.replace(/[^\w]+/, '').charAt(0)}
        </H1Text>
      );
    }

    const img = <img src={image} alt={link.value} />;

    if (lazyLoadImage) {
      return <Lazy offset={0}>{img}</Lazy>;
    }

    return img;
  }, [lazyLoadImage, link.imageUrl, link.value]);

  return (
    <Component
      selected={selected}
      data-test="NavigationLink"
      data-cy={link.value}
    >
      <MediaButton
        media={<S.ListItemImage>{children}</S.ListItemImage>}
        url={(link.indexable && link.path) || undefined}
        onClick={onClickHandler}
      >
        <S.ListItemText>
          <S.LimitedTextField>
            <TitleSmallText color={FontColor.DARK}>{link.value}</TitleSmallText>
          </S.LimitedTextField>
        </S.ListItemText>
      </MediaButton>
    </Component>
  );
};

export default NavigationLink;
